// src/components/TimesheetViewModal.jsx
import React, { useRef, useEffect } from "react";
import moment from "moment";
import { 
  FaUser, FaBuilding, FaTag, FaCalendarWeek, 
  FaHourglassHalf, FaFileAlt, FaCheckCircle, FaTimesCircle, 
  FaProjectDiagram, FaInfoCircle, FaTimes, FaExclamationTriangle 
} from "react-icons/fa";

const TimesheetViewModal = ({ timesheet, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    if (timesheet) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [timesheet, onClose]);

  if (!timesheet) return null;

  const getStatusClasses = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-orange-100 text-orange-800 border-orange-200';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <div
        ref={modalRef}
        className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-600 to-orange-500 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 rounded-2xl">
                <FaFileAlt className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-black text-white uppercase tracking-tight">Timesheet Review</h2>
                <p className="text-orange-100 text-xs font-bold uppercase tracking-widest">ID: {timesheet._id?.slice(-8)}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full text-white transition-colors">
              <FaTimes size={24} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-grow overflow-y-auto px-8 py-6 space-y-6">
          
          {/* Rejection Reason Alert */}
          {timesheet.status === 'rejected' && (
            <div className="bg-red-50 border-2 border-red-100 p-4 rounded-2xl flex items-start gap-3">
              <FaExclamationTriangle className="text-red-500 mt-1" />
              <div>
                <p className="text-sm font-black text-red-800 uppercase">Rejection Reason</p>
                <p className="text-red-600 text-sm">{timesheet.rejectionReason || "No reason specified."}</p>
              </div>
            </div>
          )}

          {/* Employee Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Employee</p>
              <div className="flex items-center gap-2">
                <FaUser className="text-orange-500" />
                <span className="font-bold text-gray-800">{timesheet.name}</span>
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Department</p>
              <div className="flex items-center gap-2">
                <FaBuilding className="text-orange-500" />
                <span className="font-bold text-gray-800">{timesheet.department}</span>
              </div>
            </div>
          </div>

          {/* Status Banner */}
          <div className={`flex items-center justify-between p-4 rounded-2xl border-2 ${getStatusClasses(timesheet.status)}`}>
            <div className="flex items-center gap-3">
              {timesheet.status === 'approved' ? <FaCheckCircle size={20}/> : <FaHourglassHalf size={20}/>}
              <span className="font-black uppercase tracking-tight">{timesheet.status}</span>
            </div>
            <div className="text-right text-[10px] font-bold uppercase opacity-60">
              Submitted: {moment(timesheet.createdAt).format('LL')}
            </div>
          </div>

          {/* Table */}
          <div className="rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Project</th>
                  <th className="px-4 py-3">Activity</th>
                  <th className="px-4 py-3 text-right">Hrs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {timesheet.entries?.map((entry, i) => (
                  <tr key={i} className="text-sm">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{moment(entry.date).format('MMM DD')}</td>
                    <td className="px-4 py-3 font-bold text-orange-600">{entry.project}</td>
                    <td className="px-4 py-3 text-gray-700">{entry.activity}</td>
                    <td className="px-4 py-3 text-right font-black">{entry.hours}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-orange-50/50">
                <tr>
                  <td colSpan="3" className="px-4 py-3 text-right font-bold text-gray-500 uppercase text-xs">Total Tracked Hours</td>
                  <td className="px-4 py-3 text-right font-black text-orange-600 text-lg">{timesheet.totalHours || 0}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Documents */}
          {timesheet.supportingDocs?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Supporting Documents</h3>
              <div className="grid grid-cols-1 gap-2">
                {timesheet.supportingDocs.map((doc, idx) => (
                  <a key={idx} href={doc.fileUrl} target="_blank" rel="noreferrer" 
                     className="flex items-center p-3 bg-gray-50 rounded-xl hover:bg-orange-50 transition-colors border border-gray-100 group">
                    <FaFileAlt className="text-gray-400 group-hover:text-orange-500 mr-3" />
                    <span className="text-sm font-medium text-gray-700 truncate">{doc.fileName || 'Attachment'}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-8 py-3 bg-white border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-100 transition-all">
            Dismiss Review
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimesheetViewModal;